import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = ({ token }) => {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('form'); // form | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setErrorMsg('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setErrorMsg('Password must be at least 6 characters');
      return;
    }
    setErrorMsg('');
    setStatus('loading');
    try {
      const resp = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Reset failed');
      setUserData(data);
      setStatus('success');
      setTimeout(() => navigate('/app', { replace: true }), 2000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {(status === 'form' || status === 'loading') && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-white text-lg font-semibold" data-testid="reset-title">Set new password</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="password" placeholder="New password (min 6 characters)" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                data-testid="reset-password" required minLength={6}
              />
              <input
                type="password" placeholder="Confirm password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                data-testid="reset-confirm" required
              />
              {errorMsg && <p className="text-red-400 text-xs" data-testid="reset-error">{errorMsg}</p>}
              <Button
                type="submit" disabled={status === 'loading'}
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
                data-testid="reset-submit"
              >
                {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          </div>
        )}
        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-white text-xl font-semibold mb-2">Password Reset!</h2>
            <p className="text-white/50 text-sm">Redirecting you to the app...</p>
          </div>
        )}
        {status === 'error' && (
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-white text-xl font-semibold mb-2">Reset Failed</h2>
            <p className="text-white/50 text-sm mb-4">{errorMsg}</p>
            <Button onClick={() => navigate('/app', { replace: true })} className="bg-purple-600 hover:bg-purple-700 text-white text-sm">
              Go to App
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
